import axios from 'axios';
import { createDPoPHeaders, isDPoPSupported } from './dpop';
import { OAUTH_CLIENTS } from '@edusense/shared-constants';
import { buildRefreshTokenRequestParams } from './refresh-request';
export { buildRefreshTokenRequestParams } from './refresh-request';

// --- Constants (필요한 것만) ---
const PROXY_TOKEN_ENDPOINT_PATH = '/api/proxy/token'; // 경로만 상수로 정의

// 중복 처리 방지를 위한 전역 상태
const ongoingRefreshOperations = new Map<string, Promise<any>>();

interface TokenResponse {
    access_token: string;
    refresh_token?: string; // 새 리프레시 토큰이 발급될 수도 있음
    id_token?: string;
    token_type: string;
    expires_in: number;
    scope?: string;
}

// Refresh Result Interface
export interface RefreshResult {
    success: boolean;
    accessToken?: string;
    refreshToken?: string; // 반환 값에 리프레시 토큰 포함
    expiresIn?: number; // 만료 시간(초) 추가
    error?: string; // 에러 종류 식별용 추가
    tokenType?: string; // 토큰 타입 추가 (Bearer 또는 DPoP)
}

// --- Main Refresh Token Handler ---
export const handleRefreshTokenFlow = async (
    refreshToken: string,
    authServerUrl: string, // authServerUrl 인자 추가
    existingTokenType?: string, // 기존 토큰 타입 추가 (DPoP 지원용)
    clientId: string = OAUTH_CLIENTS.APP_CLIENT.clientId
): Promise<RefreshResult> => {
    console.log('[RefreshTokenHandler] Refresh Token Flow 시도 (via Proxy)...', { clientId });

    if (!refreshToken) {
        console.warn('[RefreshTokenHandler] Refresh token이 제공되지 않았습니다.');
        return { success: false };
    }

    // 중복 처리 방지: 동일한 토큰으로 진행 중인 갱신이 있는지 확인
    const refreshKey = `${refreshToken.substring(0, 20)}_${authServerUrl}_${clientId}`;
    
    if (ongoingRefreshOperations.has(refreshKey)) {
        console.log('[RefreshTokenHandler] Refresh operation already in progress, waiting for existing operation');
        return ongoingRefreshOperations.get(refreshKey)!;
    }

    const PROXY_TOKEN_ENDPOINT = `${authServerUrl}${PROXY_TOKEN_ENDPOINT_PATH}`;

    // 진행 중인 갱신 작업을 저장
    const refreshOperation = (async (): Promise<RefreshResult> => {
        try {
            const tokenParams = buildRefreshTokenRequestParams(refreshToken, clientId);

            console.log('[RefreshTokenHandler] 프록시 토큰 갱신 요청 시작:', PROXY_TOKEN_ENDPOINT);

            // DPoP 지원 확인
            const dpopSupported = isDPoPSupported();
            const useDPoP = dpopSupported && existingTokenType?.toLowerCase() === 'dpop';
            
            // DPoP 헤더 생성 (선택적)
            let headers: Record<string, string> = { 'Content-Type': 'application/x-www-form-urlencoded' };
            if (useDPoP) {
                try {
                    headers = await createDPoPHeaders('POST', PROXY_TOKEN_ENDPOINT, undefined, headers);
                    console.log('[RefreshTokenHandler] DPoP header added for refresh');
                } catch (error) {
                    console.warn('[RefreshTokenHandler] DPoP header creation failed:', error);
                    // DPoP 생성 실패 시에도 계속 진행
                }
            }

            const tokenResponse = await axios.post<TokenResponse>(
                PROXY_TOKEN_ENDPOINT,
                tokenParams,
                { headers }
            );
            console.log('[RefreshTokenHandler] 프록시 토큰 갱신 응답 수신:', {
                hasAccessToken: Boolean(tokenResponse.data?.access_token),
                hasRefreshToken: Boolean(tokenResponse.data?.refresh_token),
                tokenType: tokenResponse.data?.token_type,
                expiresIn: tokenResponse.data?.expires_in,
            });

            if (tokenResponse.data && typeof tokenResponse.data === 'object' && 'error' in tokenResponse.data) {
                console.error('[RefreshTokenHandler] Proxy returned an error:', tokenResponse.data);
                const errorData = tokenResponse.data as { error: string, error_description?: string };
                if (errorData.error === 'invalid_grant') {
                    console.warn('[RefreshTokenHandler] Refresh token is invalid or expired (via proxy).');
                    return { success: false, error: 'invalid_grant' };
                }
                return { success: false, error: errorData.error };
            }

            const { access_token, refresh_token: newRefreshToken, expires_in, token_type } = tokenResponse.data; // token_type 추가

            if (!access_token) {
                console.error('[RefreshTokenHandler] 프록시 응답에 access_token이 없습니다.', tokenResponse.data);
                throw new Error('프록시 응답에 access_token이 없습니다.');
            }

            console.log('[RefreshTokenHandler] Refresh Token Flow 성공 (via Proxy).');
            console.log('[RefreshTokenHandler] Token type:', token_type);
            
            return {
                success: true,
                accessToken: access_token,
                refreshToken: newRefreshToken,
                expiresIn: expires_in, // expiresIn 반환
                tokenType: token_type // token_type 반환 추가
            };

        } catch (error) {
            console.error('[RefreshTokenHandler] Refresh Token Flow 실패 (Proxy):', error);
            if (axios.isAxiosError(error)) {
                console.error('[RefreshTokenHandler] 프록시 토큰 갱신 오류 응답 (Axios):', error.response?.data, error.response?.status);
                if (error.response?.data?.error === 'invalid_grant') {
                    console.warn('[RefreshTokenHandler] Refresh token is invalid or expired (via proxy - Axios).');
                    return { success: false, error: 'invalid_grant' };
                }
                return { success: false, error: error.response?.data?.error || `proxy_request_failed_${error.response?.status}` };
            }
            return { success: false, error: 'network_error' };
        }
    })().finally(() => {
        // 완료 후 진행 중인 작업에서 제거
        ongoingRefreshOperations.delete(refreshKey);
    });

    ongoingRefreshOperations.set(refreshKey, refreshOperation);
    return refreshOperation;
};
